import * as _ from 'lodash';
import { useState } from 'react';
import { Settings, ViewerBoardField } from '@gorilla/widgets-viewer/app/viewer/types';
import { ToggleSetting } from './ToggleSetting';
import { DATE_FORMATS, TIME_FORMATS } from '../constants';
import { Text, Dropdown, Flex, Button, Box, Label } from '@vibe/core';
import { dropdownValue } from '@gorilla/ui/src/components/dropdownValue';
import { AppModal, AppModalContent } from '@gorilla/ui/src/components/AppModal';
import { Heading } from '@vibe/core';

type ColumnSettings = Settings['columnSettings'][string];

type ColumnSettingsModalProps = {
  show: boolean;
  columnSettings: ColumnSettings;
  field: ViewerBoardField;
  onClose: (columnSettings?: ColumnSettings) => void;
};

export function ColumnSettingsModal({ show, field, onClose, columnSettings = {} }: ColumnSettingsModalProps) {
  const [draft, setDraft] = useState(_.cloneDeep(columnSettings));
  let selectedDateFormatOption = DATE_FORMATS[0];

  if (draft.dateFormat && DATE_FORMATS.find((option) => option.value === draft.dateFormat)) {
    selectedDateFormatOption = DATE_FORMATS.find((option) => option.value === draft.dateFormat)!;
  }

  let selectedTimeFormatOption = TIME_FORMATS[0];

  if (draft.timeFormat && TIME_FORMATS.find((option) => option.value === draft.timeFormat)) {
    selectedTimeFormatOption = TIME_FORMATS.find((option) => option.value === draft.timeFormat)!;
  }

  return (
    <AppModal
      contentSpacing={false}
      alertDialog={true}
      data-testid="columnSettingsModal"
      width={300}
      show={show}
      onClose={() => {
        onClose();
      }}
    >
      <AppModalContent>
        <Box marginBottom="large">
          <Flex gap={5} direction="column" justify="start" align="start" style={{ marginTop: '5px', marginBottom: '20px' }}>
            <Heading type="h3" weight="bold" align="start" ellipsis style={{ maxWidth: '270px' }}>
              {field.label}
            </Heading>
            <Label
              text={`ID: ${field.id}`}
              color="dark"
              kind="line"
              onClick={() => {
                if (navigator && navigator.clipboard) {
                  navigator.clipboard.writeText(field.id);
                }
              }}
            />
          </Flex>
          <Flex gap={8} direction="column" align="start" style={{ width: '100%' }}>
            <div style={{ width: 'inherit' }}>
              <div style={{ marginBottom: '8px' }}>
                <ToggleSetting
                  title="Show label"
                  value={!!draft.showLabel}
                  onChange={(value) => {
                    setDraft({
                      ...draft,
                      showLabel: value,
                    });
                  }}
                />
              </div>
              <div style={{ marginBottom: '8px' }}>
                <ToggleSetting
                  title="Display label inline"
                  value={draft.labelMode === 'inline'}
                  onChange={(value) => {
                    setDraft({
                      ...draft,
                      labelMode: value ? 'inline' : 'above',
                    });
                  }}
                />
              </div>
              <div style={{ marginBottom: '8px' }}>
                <ToggleSetting
                  title="Exclude from search"
                  value={!!draft.excludeFromSearch}
                  onChange={(value) => {
                    setDraft({
                      ...draft,
                      excludeFromSearch: value,
                    });
                  }}
                />
              </div>
              {field.type === 'status' || field.type === 'dropdown' ? (
                <div style={{ marginBottom: '8px' }}>
                  <ToggleSetting
                    title="Show as filter"
                    value={!!draft.showAsFilter}
                    onChange={(value) => {
                      setDraft({
                        ...draft,
                        showAsFilter: value,
                      });
                    }}
                  />
                </div>
              ) : (
                <></>
              )}
              {field.type === 'file' ? (
                <div style={{ marginBottom: '8px' }}>
                  <ToggleSetting
                    title="Display thumbnails"
                    value={draft.filesDisplayMode === 'thumbs'}
                    onChange={(value) => {
                      setDraft({
                        ...draft,
                        filesDisplayMode: value ? 'thumbs' : 'list',
                      });
                    }}
                  />
                </div>
              ) : (
                <></>
              )}
              {field.type === 'mirror' ? (
                <div style={{ marginBottom: '8px' }}>
                  <ToggleSetting
                    title="Summarize Numbers"
                    value={!!draft.summarizeNumbers}
                    onChange={(value) => {
                      setDraft({
                        ...draft,
                        summarizeNumbers: value,
                      });
                    }}
                  />
                </div>
              ) : (
                <></>
              )}
              {field.type === 'long_text' ? (
                <div style={{ marginBottom: '8px' }}>
                  <ToggleSetting
                    title="Enforce line breaks"
                    value={!!draft.enforceLineBreaks}
                    onChange={(value) => {
                      setDraft({
                        ...draft,
                        enforceLineBreaks: value,
                      });
                    }}
                  />
                </div>
              ) : (
                <></>
              )}
              {field.type === 'date' ? (
                <>
                  <div style={{ marginTop: '24px' }}>
                    <Text type="text2" style={{ marginBottom: '4px' }}>
                      Choose date format
                    </Text>
                    <Dropdown
                      searchable
                      onChange={(newOption: any) => {
                        setDraft({
                          ...draft,
                          dateFormat: newOption.value,
                        });
                      }}
                      size="medium"
                      options={DATE_FORMATS}
                      value={dropdownValue(selectedDateFormatOption)}
                      clearable={false}
                    />
                  </div>
                  <div style={{ marginTop: '16px' }}>
                    <Text type="text2" style={{ marginBottom: '4px' }}>
                      Choose time format
                    </Text>
                    <Dropdown
                      searchable
                      onChange={(newOption: any) => {
                        setDraft({
                          ...draft,
                          timeFormat: newOption.value,
                        });
                      }}
                      size="medium"
                      options={TIME_FORMATS}
                      value={dropdownValue(selectedTimeFormatOption)}
                      clearable={false}
                    />
                  </div>
                </>
              ) : (
                <></>
              )}
            </div>
          </Flex>
        </Box>
        <Box marginBottom="small">
          <Flex gap={16}>
            <Button
              size="medium"
              kind="primary"
              color="primary"
              style={{
                width: '100%',
              }}
              onClick={() => {
                onClose(draft);
              }}
            >
              Apply
            </Button>
          </Flex>
        </Box>
      </AppModalContent>
    </AppModal>
  );
}
